import Handsontable from 'handsontable';

function validateAllergens(cellText: string): {
    valid: string[],
    invalid: string[]
} {
    const tags = cellText
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);

    const valid = tags.filter(tag => window.FoodiloConfig.allergies.includes(tag));
    const invalid = tags.filter(tag => !window.FoodiloConfig.allergies.includes(tag));

    return {valid, invalid};
}

function validateDropdownCell(cellValue: string, source: string[]): {
    valid: boolean;
    value: string;
} {
    const value = cellValue?.trim() || '';
    return {
        valid: source.includes(value),
        value,
    };
}

function dropdownRenderer(
    instance: Handsontable,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: string,
    cellProperties: Handsontable.CellProperties
) {
    let source: string[] = [];

    if (Array.isArray(cellProperties.source)) {
        source = cellProperties.source.map(String); // prevod na string[]
    }

    const { valid } = validateDropdownCell(value, source);

    td.innerHTML = '';

    const span = document.createElement('span');
    span.textContent = value;
    span.style.display = 'inline-block';
    span.style.padding = '2px 6px';
    span.style.borderRadius = '3px';
    span.style.fontSize = '12px';
    span.style.color = 'white';
    span.style.backgroundColor = valid ? '#3182ce' : '#e53e3e'; // modrá = valid, červená = neplatná

    td.appendChild(span);
    return td;
}

function allergyRenderer(
    instance: any, td: {
        innerHTML: string;
        appendChild: (arg0: HTMLSpanElement) => void;
    }, row: any, col: any, prop: any, value: any, cellProperties: any
) {
    const { valid, invalid } = validateAllergens(value || '');

    td.innerHTML = '';

    [...valid, ...invalid].forEach((tag) => {
        const span = document.createElement('span');
        span.textContent = tag;
        span.style.display = 'inline-block';
        span.style.padding = '2px 6px';
        span.style.margin = '2px';
        span.style.borderRadius = '3px';
        span.style.fontSize = '12px';
        span.style.color = 'white';
        span.style.backgroundColor = valid.includes(tag) ? '#369763' : '#C71F37'; // Zelená vs. červená
        td.appendChild(span);
    });

    return td;
}

/**
 * Pomocná funkcia na zistenie duplicít.
 *
 * @param data
 * @param field
 */
function getDuplicateNames(data: any[], field: string): Set<string> {
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    for (const row of data) {
        const value = row[field]?.trim();
        if (!value) continue;

        if (seen.has(value)) {
            duplicates.add(value);
        } else {
            seen.add(value);
        }
    }

    return duplicates;
}

/**
 * Renderer pre name stĺpec.
 *
 * @param instance
 * @param td
 * @param row
 * @param col
 * @param prop
 * @param value
 * @param cellProperties
 */
function duplicateNameRenderer(
    instance: Handsontable,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: string,
    cellProperties: Handsontable.CellProperties
) {
    const data = instance.getSourceData();
    const duplicates = getDuplicateNames(data, prop.toString());

    td.textContent = value;

    if (duplicates.has(value)) {
        td.style.backgroundColor = '#fed7d7'; // svetločervené pozadie
        td.style.color = '#c53030'; // červený text
        td.style.fontWeight = 'bold';
    } else {
        td.style.backgroundColor = '';
        td.style.color = '';
        td.style.fontWeight = '';
    }

    return td;
}

type HandsontableRenderer = (
    instance: Handsontable,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: any,
    cellProperties: Handsontable.CellProperties
) => HTMLTableCellElement;

export const imageRenderer: HandsontableRenderer = function (
    instance: Handsontable.Core,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: any,
    cellProperties: Handsontable.CellProperties
): HTMLTableCellElement {
    // Použijeme default text renderer, ako základ (pre výmaz, formátovanie atď.)
    Handsontable.renderers.TextRenderer(instance, td, row, col, prop, '', cellProperties);

    // Vyčistíme TD
    td.innerHTML = '';

    if (typeof value === 'string' && value.startsWith('http')) {
        const img = document.createElement('img');
        img.src = value;
        img.style.maxHeight = '35px';
        img.style.maxWidth = '35px';
        img.style.display = 'block';
        img.style.objectFit = 'contain';

        td.appendChild(img);
    } else {
        td.innerText = '';
    }

    return td;
};

function isValidImageUrl(url: any) {
    const pattern = /^https?:\/\/.+\.(jpg|png)$/i;
    return pattern.test(url);
}

export function renderPreviewTable(containerId: string, data: any[][]) {

    const container = document.getElementById(containerId);
    if (!container) {
        console.warn(`[Foodilo] Container #${containerId} not found`);
        return;
    }

    // Odstráň existujúcu tabuľku (ak existuje)
    container.innerHTML = '';

    console.log(window.FoodiloConfig.allergies);

    return new Handsontable(container, {
        licenseKey: 'non-commercial-and-evaluation', // ✅ free license key
        data: data,
        colHeaders: ['Typ jedla / Chod', 'Názov jedla', 'Cena', 'Hmotnosť', 'Stravovací režim', 'Alergény', 'Popis', 'Obrázok'],
        rowHeaders: true,
        stretchH: 'all',
        manualColumnResize: true,
        manualRowResize: true,
        columnSorting: false,
        dropdownMenu: false,
        filters: false,
        contextMenu: false,
        manualColumnMove: false,
        manualRowMove: false,
        width: '100%',
        height: 650,
        autoWrapRow: true,
        autoWrapCol: true,
        columns: [
            {
                type: 'dropdown', // Typ jedla
                validator: function (value, callback) {
                    let source: unknown = this.source;
                    if (Array.isArray(source)) {
                        const strSource = source.map(String);
                        callback(validateDropdownCell(value, strSource).valid);
                    } else {
                        callback(false);
                    }
                },
                renderer: dropdownRenderer,
                source: window.FoodiloConfig.foodType,
            },
            {
                type: 'text',
                renderer: duplicateNameRenderer,
                validator: function (value, callback) {
                    const data = this.instance.getSourceData();
                    const duplicates = getDuplicateNames(data, 'name');
                    callback(!duplicates.has(value));
                }
            },
            {
                type: 'numeric',
                numericFormat: {pattern: '0.00'},
                language: 'sk',
                allowEmpty: true
            },
            {
                type: 'text',
                allowEmpty: true
            },
            {
                type: 'dropdown', // Typ stravy
                validator: function (value, callback) {
                    let source: unknown = this.source;
                    // Ak je to pole string alebo čísel, použijeme ho (a prevedieme na stringy)
                    if (Array.isArray(source)) {
                        const strSource = source.map(String);
                        callback(validateDropdownCell(value, strSource).valid);
                    } else {
                        // fallback pre funkciu alebo undefined
                        callback(false);
                    }
                },
                renderer: dropdownRenderer,
                source: window.FoodiloConfig.mealTypes,
            },
            {
                renderer: allergyRenderer,
                validator: function (value, callback) {
                    const {invalid} = validateAllergens(value);
                    callback(invalid.length === 0);
                }
            },
            {
                type: 'text',
                allowEmpty: true
            },
            {renderer: imageRenderer} // obrázok
        ],
        cells: function (row, col) {
            const cellProperties: any = {};

            // Príklad validácie: tretí stĺpec (index 2) musí byť číslo väčšie, ako 0
            if (col === 5) {
                cellProperties.validator = function (value: any, callback: (valid: boolean) => void) {
                    if (value) {
                        const valid = isValidImageUrl(value);
                        callback(valid);
                    }
                };
            }

            return cellProperties;
        },
        afterValidate(isValid, value, row, prop, source) {
            if (!isValid) {
                console.warn(`❌ Neplatná hodnota v [${row}, ${prop}]: ${value}`);
            } else {

            }
        },
    });
}
